import { Link } from "react-router-dom";
import { Facebook, MessageCircle, Mail, Phone, MapPin } from "lucide-react";
import { useSettings } from "@/lib/providers";
import { waLink } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { FaqList } from "@/components/site/Faq";
import { Star, Spiral } from "@/components/site/Doodles";

const PageHead = ({ title, sub }) => (
  <header className="relative mb-12">
    <Star className="absolute -top-6 left-0 w-12 h-12" />
    <h1 className="font-display text-5xl sm:text-6xl text-ink">{title}</h1>
    {sub && <p className="text-soft mt-6 max-w-2xl">{sub}</p>}
  </header>
);

const Wrap = ({ children, testid, narrow }) => (
  <div className={`${narrow ? "max-w-3xl" : "max-w-6xl"} mx-auto px-4 sm:px-6 py-16`} data-testid={testid}>{children}</div>
);

export function About() {
  useSeo("عن الأكاديمية", "تعرف على أكاديمية خرابيش لتعليم الموشن جرافيك والأنيميشن.");
  const { settings: s } = useSettings();
  return (
    <Wrap testid="about-page">
      <PageHead title="عن خرابيش" sub="أكاديمية عربية متخصصة في تعليم الموشن جرافيك والأنيميشن بكورسات مسجلة عملية." />
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="prose-ar about-text">
          <h2>إحنا مين؟</h2>
          <p>أكاديمية خرابيش مستوحاة من روح وهوية خرابيش موشن — مكان بيحوّل الرسومات البسيطة والأفكار المجنونة لحكايات متحركة.</p>
          <p>بنقدم كورسات مسجلة بالعربي، مقسمة لوحدات ودروس قصيرة ومركزة، بحيث تقدر تتعلم بسرعتك وترجع لأي درس وقت ما تحتاج.</p>
          <h2>رسالتنا</h2>
          <p>إن أي حد عنده شغف بالحركة يلاقي محتوى عربي محترم يوصله من الصفر لمستوى احترافي، ويطلع بمشاريع حقيقية يفتخر بيها.</p>
        </div>
        <div className="relative rounded-[2rem] bg-white sticker p-8 space-y-3">
          <Spiral className="absolute -top-8 -left-8 w-24 h-24" />
          <p className="text-sm text-soft">المدرب</p>
          <p className="font-heading text-3xl font-bold text-ink">{s.instructor_name}</p>
          <p className="text-brand">{s.instructor_title}</p>
          <p className="text-soft leading-relaxed">{s.instructor_bio}</p>
          <Link to="/courses" className="btn-pop btn-primary inline-block mt-4 px-6 py-3 rounded-full font-bold">شوف الكورسات</Link>
        </div>
      </div>
    </Wrap>
  );
}

export function Contact() {
  useSeo("تواصل معنا", "تواصل مع أكاديمية خرابيش عبر واتساب أو فيسبوك.");
  const { settings: s } = useSettings();
  const items = [
    s.whatsapp_number && [MessageCircle, "واتساب الدعم", s.whatsapp_number, waLink(s.whatsapp_number), "#7F2BC6", "contact-whatsapp"],
    s.facebook_url && [Facebook, "فيسبوك", "KharabeeshAcademy", s.facebook_url, "#9D5BDB", "contact-facebook"],
    s.contact_email && [Mail, "البريد الإلكتروني", s.contact_email, `mailto:${s.contact_email}`, "#7F2BC6", "contact-email"],
    s.contact_phone && [Phone, "تليفون", s.contact_phone, `tel:${s.contact_phone}`, "#9D5BDB", "contact-phone"],
  ].filter(Boolean);
  return (
    <Wrap testid="contact-page">
      <PageHead title="تواصل معنا" sub="عندك سؤال عن كورس أو مشكلة في الدفع؟ كلمنا وهنرد عليك في أسرع وقت." />
      <div className="grid sm:grid-cols-2 gap-6">
        {items.map(([Icon, t, v, href, c, id]) => (
          <a key={id} href={href} target="_blank" rel="noreferrer" data-testid={id}
            className="group rounded-3xl bg-white sticker p-7 flex items-center gap-5 hover:-translate-y-1 transition-transform">
            <span className="grid place-items-center w-16 h-16 rounded-2xl" style={{ background: c }}><Icon className="text-white" size={28} /></span>
            <span><span className="block text-sm text-soft">{t}</span><span className="block font-heading text-xl font-bold text-ink" dir="ltr">{v}</span></span>
          </a>
        ))}
      </div>
      {s.address && <p className="mt-8 flex items-center gap-2 text-soft"><MapPin size={18} />{s.address}</p>}
    </Wrap>
  );
}

export function FaqPage() {
  useSeo("الأسئلة الشائعة", "إجابات على أكثر الأسئلة الشائعة عن كورسات أكاديمية خرابيش والدفع والتفعيل.");
  return (
    <Wrap testid="faq-page" narrow>
      <PageHead title="الأسئلة الشائعة" />
      <FaqList />
    </Wrap>
  );
}

export function Privacy() {
  useSeo("سياسة الخصوصية");
  return (
    <Wrap testid="privacy-page" narrow>
      <PageHead title="سياسة الخصوصية" />
      <div className="prose-ar">
        <p>[نص مبدئي — يُرجى مراجعته مع مستشار قانوني قبل الإطلاق]</p>
        <h2>البيانات التي نجمعها</h2>
        <p>الاسم، البريد الإلكتروني، رقم الموبايل (اختياري)، بيانات طلبات الدفع وصور إيصالات التحويل، وسجل تقدمك في الدروس.</p>
        <h2>كيف نستخدم البيانات</h2>
        <p>لإنشاء حسابك، مراجعة المدفوعات وتفعيل الكورسات، متابعة تقدمك، والتواصل معك بخصوص حسابك. لا نبيع بياناتك لأي طرف ثالث.</p>
        <h2>حماية البيانات</h2>
        <p>كلمات المرور مشفّرة، وإيصالات الدفع محفوظة في تخزين خاص لا يمكن الوصول إليه إلا لصاحب الطلب وإدارة الأكاديمية.</p>
        <h2>العلامة المائية</h2>
        <p>لحماية المحتوى، يظهر بريدك الإلكتروني أو اسمك كعلامة مائية متحركة أثناء مشاهدة الدروس المدفوعة.</p>
        <h2>حقوقك</h2>
        <p>تقدر تطلب تعديل أو حذف بياناتك في أي وقت بالتواصل معنا عبر صفحة التواصل.</p>
      </div>
    </Wrap>
  );
}

export function Terms() {
  useSeo("الشروط والأحكام");
  return (
    <Wrap testid="terms-page" narrow>
      <PageHead title="الشروط والأحكام" />
      <div className="prose-ar">
        <p>[نص مبدئي — يُرجى مراجعته مع مستشار قانوني قبل الإطلاق]</p>
        <h2>الحساب</h2>
        <p>الحساب شخصي ولا يجوز مشاركته مع أي شخص آخر. يحق للأكاديمية إيقاف أي حساب يتم مشاركته أو إساءة استخدامه.</p>
        <h2>الدفع والتفعيل</h2>
        <p>يتم الدفع يدوياً عبر فودافون كاش أو إنستاباي، ويتم تفعيل الكورس بعد مراجعة إيصال التحويل والتأكد منه. رفع إيصالات غير صحيحة يؤدي لرفض الطلب.</p>
        <h2>الملكية الفكرية</h2>
        <p>جميع الفيديوهات والمواد التعليمية مملوكة لأكاديمية خرابيش. يُمنع تحميل أو تسجيل أو إعادة نشر أو بيع أي جزء من المحتوى.</p>
        <h2>الاسترداد</h2>
        <p>[سياسة الاسترداد — حددها من هنا]</p>
        <h2>التعديلات</h2>
        <p>يحق للأكاديمية تعديل هذه الشروط، ويتم نشر أي تعديل على هذه الصفحة.</p>
      </div>
    </Wrap>
  );
}

export function NotFound() {
  useSeo("الصفحة غير موجودة");
  return (
    <div className="text-center py-32 space-y-5" data-testid="not-found-page">
      <p className="font-display text-8xl text-[var(--kh-violet)]">404</p>
      <p className="text-soft">الصفحة دي اتخربشت ومش موجودة.</p>
      <Link to="/" className="btn-pop btn-primary inline-block px-6 py-3 rounded-full font-bold">الرئيسية</Link>
    </div>
  );
}
